/**
 * Origem Flow: ações locais do ⌘K (paleta de comandos).
 *
 * Tudo aqui é calculado de forma síncrona a partir do store e da rota — nada
 * vai ao servidor — para a lista responder em menos de 100 ms enquanto a
 * pessoa digita. A busca remota do WorkspaceSearchModal continua existindo
 * logo abaixo das ações.
 *
 * Uma ação é `{ key, icon, title, subtitle, keywords, hint, run }`. `run`
 * recebe `{ router, store, bus }` e executa (navegar, criar, atualizar).
 */
import {
  getStatusField,
  isDoneOption,
} from '@baserow/modules/database/utils/dueDate'
import { getAssigneeField } from '@baserow/modules/database/utils/subtasks'
import { cachedViewsOf } from '@baserow/modules/core/search/viewsCache'
import {
  parseNaturalTask,
  toTaskIntent,
} from '@baserow/modules/core/search/naturalTask'

const PRIORITY_FIELD_NAMES = new Set(['prioridade', 'priority'])

// Criação pendente: "Nova tarefa em X" a partir de outra página navega até a
// tabela e a Table.vue consome isto ao montar/trocar de tabela.
let pendingAddRow = null

export function setPendingAddRow(tableId, title = '', intent = null) {
  pendingAddRow = { tableId, title, intent, at: Date.now() }
}

export function consumePendingAddRow(tableId) {
  if (
    pendingAddRow &&
    pendingAddRow.tableId === tableId &&
    Date.now() - pendingAddRow.at < 15000
  ) {
    const pending = pendingAddRow
    pendingAddRow = null
    return pending
  }
  return null
}

/**
 * Normaliza para comparação: minúsculas e sem acentos ("Conclúido" casa
 * com "concluido").
 */
export function normalize(text) {
  return (text || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

/**
 * Interpreta "+ Título", "nova tarefa Título" ou "criar tarefa Título" como
 * pedido de criação com título. Devolve `{ title }` ou null.
 */
export function parseNewTaskQuery(query) {
  const raw = (query || '').trim()
  if (!raw) {
    return null
  }
  if (raw.startsWith('+')) {
    return { title: raw.slice(1).trim() }
  }
  const match = raw.match(/^(nova|novo|criar|new)\s+(tarefa|task)\b\s*(.*)$/i)
  if (match) {
    return { title: (match[3] || '').trim() }
  }
  return null
}

function getPriorityField(fields) {
  if (!Array.isArray(fields)) {
    return null
  }
  return (
    fields.find(
      (field) =>
        field.type === 'single_select' &&
        PRIORITY_FIELD_NAMES.has(normalize(field.name))
    ) || null
  )
}

function primaryFieldOf(fields) {
  return (fields || []).find((field) => field.primary) || null
}

/**
 * A linha "alvo" das ações de status/prioridade/responsável: a tarefa
 * aberta no modal (rowModal) ou, no grid, a linha da célula selecionada.
 */
// Último card do Quadro em que a pessoa clicou (o kanban não tem "célula
// selecionada"): registrado por um listener global de mousedown.
let lastKanbanRowId = null
if (typeof document !== 'undefined' && !window.__origemKanbanTargetListener) {
  window.__origemKanbanTargetListener = true
  document.addEventListener(
    'mousedown',
    (event) => {
      const card =
        event.target && event.target.closest
          ? event.target.closest('[data-row-id]')
          : null
      if (card) {
        lastKanbanRowId = parseInt(card.getAttribute('data-row-id')) || null
      }
    },
    true
  )
}

function findTargetRow(store, table) {
  if (!table || !table.id) {
    return null
  }
  const modalRows = store.state.rowModal?.rows || {}
  for (const entry of Object.values(modalRows)) {
    if (entry && entry.tableId === table.id && entry.row && entry.row.id) {
      return entry.row
    }
  }
  const gridState = store.state.page?.view?.grid
  if (gridState && Array.isArray(gridState.rows)) {
    const selected = gridState.rows.find(
      (row) => row && row._ && row._.selected
    )
    if (selected) {
      return selected
    }
  }
  // Quadro: o último card clicado, se ainda estiver no buffer.
  const stacks = store.state.page?.view?.kanban?.stacks
  if (lastKanbanRowId && stacks) {
    for (const stack of Object.values(stacks)) {
      const found = (stack?.results || []).find(
        (row) => row && row.id === lastKanbanRowId
      )
      if (found) {
        return found
      }
    }
  }
  return null
}

function rowTitle(row, fields) {
  const primary = primaryFieldOf(fields)
  const value = primary ? row[`field_${primary.id}`] : ''
  return value || ''
}

function tableRoute(table) {
  return {
    name: 'database-table',
    params: { databaseId: table.database_id, tableId: table.id },
  }
}

/**
 * Monta a lista completa de ações para o contexto atual e filtra pela
 * consulta. Sem consulta devolve as ações mais úteis do contexto.
 */
export function buildCommandActions({ store, route, registry, t, query }) {
  const workspace = store.getters['workspace/getSelected'] || {}
  if (!workspace.id) {
    return []
  }
  const q = normalize(query)
  const actions = []

  const applications = store.getters['application/getAllOfWorkspace'](workspace)
  const tables = []
  for (const application of applications) {
    if (application.type !== 'database') {
      continue
    }
    for (const table of application.tables || []) {
      // Origem Flow: o espelho "Todas as tarefas" tem o próprio atalho.
      if (table.all_tasks) {
        continue
      }
      tables.push({ ...table, database_id: application.id })
    }
  }

  const currentTableId = parseInt(route.params?.tableId) || null
  const currentTable =
    tables.find((table) => table.id === currentTableId) || null
  const fields = currentTable ? store.getters['field/getAll'] : []
  const views = currentTable ? store.getters['view/getAll'] : []
  const currentView = currentTable ? store.getters['view/getSelected'] : null
  const targetRow = currentTable ? findTargetRow(store, currentTable) : null

  // ── Conversa vira trabalho (IA propõe, você aceita) ────────────────────
  if (currentTable) {
    actions.push({
      key: `conversation-${currentTable.id}`,
      icon: 'iconoir-sparks',
      title: t('workspaceSearch.actions.conversation', {
        table: currentTable.name,
      }),
      subtitle: t('workspaceSearch.actions.conversationHint'),
      keywords:
        'conversa reunião áudio clipe transcrever ia tarefas propostas meeting',
      run: () =>
        window.dispatchEvent(
          new CustomEvent('origem-conversation-open', {
            detail: { tableId: currentTable.id },
          })
        ),
    })
  }

  // ── Criar tarefa (com linguagem natural) ───────────────────────────────
  const newTask = parseNewTaskQuery(query)
  const parsed = newTask
    ? parseNaturalTask(newTask.title, {
        tables,
        users: workspace.users || [],
      })
    : null
  const newTaskTitle = parsed ? parsed.title : ''
  const intent = parsed ? toTaskIntent(parsed) : null
  // "@Tabela" no texto escolhe a tabela-alvo; senão a atual vem primeiro.
  const preferredTable = (parsed && parsed.table) || currentTable || null
  const newTaskTables = preferredTable
    ? [
        preferredTable,
        ...tables.filter((table) => table.id !== preferredTable.id),
      ]
    : tables
  const chipsLabel = parsed
    ? parsed.chips
        .filter((chip) => chip.kind !== 'table')
        .map((chip) => chip.label)
        .join(' · ')
    : ''
  newTaskTables.forEach((table, index) => {
    actions.push({
      key: `new-task-${table.id}`,
      icon: 'iconoir-plus',
      title: newTaskTitle
        ? t('workspaceSearch.actions.newTaskTitled', {
            title: newTaskTitle,
            table: table.name,
          })
        : t('workspaceSearch.actions.newTaskIn', { table: table.name }),
      subtitle: chipsLabel || t('workspaceSearch.actions.newTaskHint'),
      chips: parsed && index === 0 ? parsed.chips : [],
      keywords: `nova tarefa criar tarefa new task + ${table.name}`,
      hint: index === 0 && currentTable ? '+' : null,
      // Sem consulta só a tabela atual aparece; com "+ título" ou "nova
      // tarefa" todas as tabelas entram para escolher onde criar.
      contextual: index === 0 ? 3 : newTask ? 2 : 0,
      matchesAny: !!newTask,
      run({ router, bus }) {
        const payload = { tableId: table.id, title: newTaskTitle, intent }
        if (currentTable && table.id === currentTable.id) {
          bus.$emit('origem-command-add-row', payload)
          return
        }
        setPendingAddRow(table.id, newTaskTitle, intent)
        router.push(tableRoute(table))
      },
    })
  })

  // ── Ações sobre a tarefa aberta/selecionada ────────────────────────────
  if (targetRow && currentView) {
    const title = rowTitle(targetRow, fields)
    const rowLabel = title || `#${targetRow.id}`
    const dispatchUpdate = async ({ store, field, value }) => {
      await store.dispatch(`page/view/${currentView.type}/updateRowValue`, {
        table: currentTable,
        view: currentView,
        fields,
        row: targetRow,
        field,
        value,
        oldValue: targetRow[`field_${field.id}`],
      })
    }
    const statusField = getStatusField(fields)
    if (statusField) {
      const current = targetRow[`field_${statusField.id}`]
      for (const option of statusField.select_options || []) {
        if (current && current.id === option.id) {
          continue
        }
        actions.push({
          key: `status-${option.id}`,
          icon: isDoneOption(option)
            ? 'iconoir-check-circle'
            : 'iconoir-circle',
          color: option.color,
          title: t('workspaceSearch.actions.setStatus', {
            value: option.value,
          }),
          subtitle: t('workspaceSearch.actions.onRow', { row: rowLabel }),
          keywords: `status ${statusField.name} ${option.value} concluir concluido done`,
          contextual: 1,
          run: ({ store }) =>
            dispatchUpdate({ store, field: statusField, value: option }),
        })
      }
    }
    const priorityField = getPriorityField(fields)
    if (priorityField) {
      const current = targetRow[`field_${priorityField.id}`]
      for (const option of priorityField.select_options || []) {
        if (current && current.id === option.id) {
          continue
        }
        actions.push({
          key: `priority-${option.id}`,
          icon: 'iconoir-triangle-flag',
          color: option.color,
          title: t('workspaceSearch.actions.setPriority', {
            value: option.value,
          }),
          subtitle: t('workspaceSearch.actions.onRow', { row: rowLabel }),
          keywords: `prioridade priority ${option.value}`,
          contextual: 0,
          run: ({ store }) =>
            dispatchUpdate({ store, field: priorityField, value: option }),
        })
      }
    }
    const assigneeField = getAssigneeField(fields)
    if (assigneeField) {
      const current = targetRow[`field_${assigneeField.id}`] || []
      for (const user of workspace.users || []) {
        const userId = user.user_id ?? user.id
        if (current.some((item) => item.id === userId)) {
          continue
        }
        actions.push({
          key: `assign-${userId}`,
          icon: 'iconoir-user',
          title: t('workspaceSearch.actions.assignTo', { name: user.name }),
          subtitle: t('workspaceSearch.actions.onRow', { row: rowLabel }),
          keywords: `atribuir responsavel assign ${user.name} ${user.email || ''}`,
          contextual: 0,
          run: ({ store }) =>
            dispatchUpdate({
              store,
              field: assigneeField,
              value: [...current, { id: userId, name: user.name }],
            }),
        })
      }
    }
  }

  // ── Navegar: views da tabela atual ─────────────────────────────────────
  if (currentTable) {
    for (const view of views) {
      if (currentView && view.id === currentView.id) {
        continue
      }
      const viewType = registry.get('view', view.type)
      actions.push({
        key: `view-${view.id}`,
        icon: viewType?.iconClass || 'iconoir-view-grid',
        title: t('workspaceSearch.actions.goToView', {
          table: currentTable.name,
          view: view.name,
        }),
        subtitle: t('workspaceSearch.actions.goToViewHint'),
        keywords: `ir para abrir view visualizacao ${view.name} ${currentTable.name}`,
        contextual: 1,
        run: ({ router }) =>
          router.push({
            name: 'database-table',
            params: {
              databaseId: currentTable.database_id,
              tableId: currentTable.id,
              viewId: view.id,
            },
          }),
      })
    }
  }

  // ── Navegar: tabelas ───────────────────────────────────────────────────
  for (const table of tables) {
    if (currentTable && table.id === currentTable.id) {
      continue
    }
    actions.push({
      key: `table-${table.id}`,
      icon: 'iconoir-table',
      title: t('workspaceSearch.actions.goToTable', { table: table.name }),
      subtitle: t('workspaceSearch.actions.goToTableHint'),
      keywords: `ir para abrir tabela cliente ${table.name}`,
      contextual: 0,
      run: ({ router }) => router.push(tableRoute(table)),
    })
    // Views de outras tabelas: vêm do cache local (preenchido ao visitar a
    // tabela), então só aparecem para tabelas já abertas neste navegador.
    for (const view of cachedViewsOf(table.id)) {
      const viewType = registry.get('view', view.type)
      actions.push({
        key: `view-${view.id}`,
        icon: viewType?.iconClass || 'iconoir-view-grid',
        title: t('workspaceSearch.actions.goToView', {
          table: table.name,
          view: view.name,
        }),
        subtitle: t('workspaceSearch.actions.goToViewHint'),
        keywords: `ir para abrir view visualizacao ${view.name} ${table.name}`,
        contextual: 0,
        run: ({ router }) =>
          router.push({
            name: 'database-table',
            params: {
              databaseId: table.database_id,
              tableId: table.id,
              viewId: view.id,
            },
          }),
      })
    }
  }

  // ── Navegar: hubs ──────────────────────────────────────────────────────
  const hubs = [
    ['workspace', 'iconoir-home-simple', 'home', 'inicio home'],
    ['workspace-planner', 'iconoir-calendar', 'planner', 'planejado agenda'],
    ['workspace-docs', 'iconoir-page', 'docs', 'documentos docs'],
    ['workspace-ai', 'iconoir-sparks', 'ai', 'ia inteligencia'],
    [
      'workspace-whiteboards',
      'iconoir-design-pencil',
      'whiteboards',
      'quadro branco',
    ],
    ['workspace-clips', 'iconoir-video-camera', 'clips', 'clipes gravacoes'],
    ['workspace-manual', 'iconoir-book', 'manual', 'manual ajuda'],
    ['settings-members', 'iconoir-group', 'members', 'membros equipe'],
  ]
  for (const [name, icon, key, keywords] of hubs) {
    if (route.name === name) {
      continue
    }
    actions.push({
      key: `hub-${key}`,
      icon,
      title: t(`workspaceSearch.actions.hubs.${key}`),
      subtitle: t('workspaceSearch.actions.goToHint'),
      keywords: `ir para abrir ${keywords}`,
      contextual: 0,
      run: ({ router }) =>
        router.push({ name, params: { workspaceId: workspace.id } }),
    })
  }

  // ── Filtro e ranking ───────────────────────────────────────────────────
  if (!q) {
    return actions
      .filter((action) => action.contextual > 0)
      .sort((a, b) => b.contextual - a.contextual)
      .slice(0, 8)
  }
  const tokens = q.split(/\s+/).filter(Boolean)
  const scored = []
  for (const action of actions) {
    if (action.matchesAny) {
      scored.push({ action, score: 100 + action.contextual })
      continue
    }
    const haystack = normalize(`${action.title} ${action.keywords}`)
    const titleNorm = normalize(action.title)
    if (!tokens.every((token) => haystack.includes(token))) {
      continue
    }
    let score = 10 + action.contextual
    if (titleNorm.startsWith(q)) {
      score += 20
    } else if (titleNorm.includes(q)) {
      score += 10
    }
    scored.push({ action, score })
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, 10)
    .map((item) => item.action)
}
